/* ============================================================
   BANANA LAGOON — theme.js
   Minimal vanilla JS. No frameworks.
   ============================================================ */
(function () {
  'use strict';

  /* ============================================================
     Page loader — show on internal navigation, hide on back/forward
     ============================================================ */
  const pageLoader = document.querySelector('[data-page-loader]');
  if (pageLoader) {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!link || !link.href) return;
      if (link.target === '_blank') return;
      if (link.hasAttribute('download')) return;
      if (link.hasAttribute('data-cart-drawer-close')) return;
      if (link.hasAttribute('data-no-loader')) return;
      if (link.hasAttribute('data-cart-open')) return;

      const href = link.getAttribute('href') || '';
      if (href.startsWith('mailto:')) return;
      if (href.startsWith('tel:')) return;
      if (href.startsWith('javascript:')) return;
      if (href.startsWith('#')) return;

      // New-tab modifiers
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

      // Cross-origin
      let linkHost;
      try {
        linkHost = new URL(link.href, window.location.href).host;
      } catch (e) {
        return;
      }
      if (linkHost !== window.location.host) return;

      // Same page (hash change only) — no loader needed
      const currentPath = window.location.href.split('#')[0];
      const targetPath = link.href.split('#')[0];
      if (targetPath === currentPath) return;

      pageLoader.setAttribute('data-show', 'true');
      pageLoader.setAttribute('aria-hidden', 'false');
    });

    // Returning via back/forward from browser cache — hide loader
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        pageLoader.removeAttribute('data-show');
        pageLoader.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /* -------- Sticky header border on scroll -------- */
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* -------- Mobile nav toggle -------- */
  const mobileToggle = document.querySelector('[data-mobile-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  if (mobileToggle && mobileNav) {
    const setNavOpen = (open) => {
      mobileNav.setAttribute('data-open', String(open));
      mobileNav.setAttribute('aria-hidden', String(!open));
      mobileToggle.setAttribute('aria-expanded', String(open));
      if (open) {
        document.body.setAttribute('data-mobile-nav-open', 'true');
      } else {
        document.body.removeAttribute('data-mobile-nav-open');
      }
    };

    mobileToggle.addEventListener('click', () => {
      const isOpen = mobileNav.getAttribute('data-open') === 'true';
      setNavOpen(!isOpen);
    });

    document.querySelectorAll('[data-mobile-nav-link]').forEach((link) => {
      link.addEventListener('click', () => setNavOpen(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && mobileNav.getAttribute('data-open') === 'true') {
        setNavOpen(false);
        mobileToggle.focus();
      }
    });

    const desktopQuery = window.matchMedia('(min-width: 900px)');
    const onDesktopChange = () => {
      if (desktopQuery.matches && mobileNav.getAttribute('data-open') === 'true') {
        setNavOpen(false);
      }
    };
    if (desktopQuery.addEventListener) {
      desktopQuery.addEventListener('change', onDesktopChange);
    } else if (desktopQuery.addListener) {
      desktopQuery.addListener(onDesktopChange);
    }
  }

  /* -------- Announcement bar rotation -------- */
  const announcementRotator = document.querySelector('[data-announcement-rotator]');
  if (announcementRotator) {
    const messages = announcementRotator.querySelectorAll('[data-announcement-message]');
    if (messages.length > 1) {
      let current = 0;
      messages.forEach((m, i) => { m.style.display = i === 0 ? '' : 'none'; });
      const interval = parseInt(announcementRotator.getAttribute('data-rotation-speed'), 10) || 5000;
      setInterval(() => {
        messages[current].style.display = 'none';
        current = (current + 1) % messages.length;
        messages[current].style.display = '';
      }, interval);
    }
  }

  /* ============================================================
     Newsletter forms — belt-and-braces Klaviyo subscription
     When [data-klaviyo-subscribe] is present on the input wrapper, the email is
     ALSO sent directly to Klaviyo's public subscribe endpoint. The normal
     Shopify form still submits so the customer record + accepts_marketing flag
     is created (Klaviyo's Shopify app picks this up via sync too).
     ============================================================ */
  document.querySelectorAll('[data-klaviyo-subscribe]').forEach((form) => {
    const apiKey = form.getAttribute('data-klaviyo-api-key');
    const listId = form.getAttribute('data-klaviyo-list-id');
    if (!apiKey || !listId) return;

    form.addEventListener('submit', () => {
      const emailInput = form.querySelector('input[type="email"]');
      if (!emailInput || !emailInput.value) return;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Banana Lagoon — Theme newsletter form',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: emailInput.value },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: listId } },
          },
        },
      };

      const url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey);
      const body = JSON.stringify(payload);

      // Fire-and-forget. Shopify form still submits normally. sendBeacon survives
      // the page-nav that follows a form submit; fall back to fetch+keepalive.
      try {
        const blob = new Blob([body], { type: 'application/json' });
        const sent = navigator.sendBeacon && navigator.sendBeacon(url, blob);
        if (!sent) {
          fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-10-15',
            },
            body: body,
            keepalive: true,
          }).catch(() => {});
        }
      } catch (e) {
        // Non-critical — Shopify form still creates the customer record
      }
    });
  });

  /* -------- Product card: secondary image preload on hover -------- */
  const cards = document.querySelectorAll('.product-card');
  cards.forEach((card) => {
    const secondary = card.querySelector('.product-card__image--secondary');
    if (!secondary) return;
    card.addEventListener(
      'mouseenter',
      () => {
        if (secondary.dataset.src && !secondary.src) {
          secondary.src = secondary.dataset.src;
        }
      },
      { once: true }
    );
  });

  /* ============================================================
     CART DRAWER
     ============================================================ */
  const cartDrawer = document.querySelector('[data-cart-drawer]');

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.setAttribute('data-open', 'true');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('data-drawer-open', 'true');
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.setAttribute('data-open', 'false');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.removeAttribute('data-drawer-open');
  }

  // Attach close handlers to any element with data-cart-drawer-close inside the drawer
  function bindDrawerCloseHandlers() {
    if (!cartDrawer) return;
    cartDrawer.querySelectorAll('[data-cart-drawer-close]').forEach((el) => {
      el.addEventListener('click', (event) => {
        // Let link clicks through — but also close the drawer
        if (!el.hasAttribute('href')) event.preventDefault();
        closeCartDrawer();
      });
    });
  }

  // Refresh the drawer's contents via Shopify Section Rendering API
  async function refreshCartDrawer() {
    if (!cartDrawer) return;
    try {
      const response = await fetch('/cart?section_id=cart-drawer');
      if (!response.ok) throw new Error('Failed to refresh cart drawer');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newPanel = doc.querySelector('[data-cart-drawer-panel]');
      const currentPanel = cartDrawer.querySelector('[data-cart-drawer-panel]');
      if (newPanel && currentPanel) {
        currentPanel.innerHTML = newPanel.innerHTML;
        bindDrawerCloseHandlers();
        bindDrawerDynamicHandlers();
      }
      updateCartCount();
    } catch (err) {
      console.error('Cart drawer refresh failed:', err);
    }
  }

  // Keep the header cart count badge in sync
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      const badge = document.querySelector('.cart-count-badge');
      const cartLink = document.querySelector('[data-cart-open]');
      if (cart.item_count > 0) {
        if (badge) {
          badge.textContent = cart.item_count;
          badge.style.display = '';
        } else if (cartLink) {
          const span = document.createElement('span');
          span.className = 'cart-count-badge';
          span.setAttribute('aria-hidden', 'true');
          span.textContent = cart.item_count;
          cartLink.appendChild(span);
        }
      } else if (badge) {
        badge.remove();
      }
    } catch (e) {
      // Non-critical — silently ignore
    }
  }

  async function addToCart(variantId, quantity) {
    const qty = quantity || 1;
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty }),
    });
    if (!response.ok) {
      let message = 'Add to cart failed';
      try {
        const err = await response.json();
        message = err.description || message;
      } catch (e) {}
      throw new Error(message);
    }
    await refreshCartDrawer();
    openCartDrawer();
    return response.json();
  }

  async function changeLineQuantity(lineKey, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    await refreshCartDrawer();
    return response.json();
  }

  // Handlers for dynamic elements inside the drawer (re-attached after each refresh)
  function bindDrawerDynamicHandlers() {
    if (!cartDrawer) return;

    // Quantity increment
    cartDrawer.querySelectorAll('[data-cart-qty-increment]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-cart-qty-increment');
        const valueEl = cartDrawer.querySelector('[data-cart-qty-value="' + key + '"]');
        const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        btn.disabled = true;
        try {
          await changeLineQuantity(key, current + 1);
        } catch (e) {
          console.error(e);
          btn.disabled = false;
        }
      });
    });

    // Quantity decrement
    cartDrawer.querySelectorAll('[data-cart-qty-decrement]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-cart-qty-decrement');
        const valueEl = cartDrawer.querySelector('[data-cart-qty-value="' + key + '"]');
        const current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        const next = Math.max(0, current - 1);
        btn.disabled = true;
        try {
          await changeLineQuantity(key, next);
        } catch (e) {
          console.error(e);
          btn.disabled = false;
        }
      });
    });

    // Remove line item
    cartDrawer.querySelectorAll('[data-cart-remove]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const key = btn.getAttribute('data-cart-remove');
        btn.disabled = true;
        try {
          await changeLineQuantity(key, 0);
        } catch (e) {
          console.error(e);
          btn.disabled = false;
        }
      });
    });

    // Add-on buttons
    cartDrawer.querySelectorAll('[data-cart-add]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const variantId = btn.getAttribute('data-cart-add');
        btn.disabled = true;
        try {
          await addToCart(variantId, 1);
        } catch (e) {
          console.error(e);
          btn.disabled = false;
        }
      });
    });
  }

  // Header cart icon → open drawer
  if (cartDrawer) {
    document.querySelectorAll('[data-cart-open]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openCartDrawer();
      });
    });

    // Initial bindings
    bindDrawerCloseHandlers();
    bindDrawerDynamicHandlers();

    // Esc to close
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && cartDrawer.getAttribute('data-open') === 'true') {
        closeCartDrawer();
      }
    });
  }

  /* ============================================================
     PDP — Gallery thumb switcher
     ============================================================ */
  const gallery = document.querySelector('[data-gallery]');
  if (gallery) {
    const mainWrap = gallery.querySelector('[data-gallery-main]');
    const mainImg = mainWrap && mainWrap.querySelector('img');
    const thumbs = gallery.querySelectorAll('[data-gallery-thumb]');

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (!mainImg) return;
        const url = thumb.getAttribute('data-image-url');
        const srcset = thumb.getAttribute('data-image-srcset');
        const alt = thumb.getAttribute('data-image-alt');
        if (url) mainImg.src = url;
        if (srcset) mainImg.srcset = srcset;
        if (alt) mainImg.alt = alt;

        thumbs.forEach((t) => {
          t.classList.toggle('is-active', t === thumb);
          t.setAttribute('aria-selected', t === thumb ? 'true' : 'false');
        });
      });
    });
  }

  /* ============================================================
     PDP — Variant picker
     ============================================================ */
  const productForm = document.querySelector('[data-product-form]');
  if (productForm) {
    const variantJsonScript = productForm.querySelector('[data-product-json]');
    let variants = [];
    try {
      variants = JSON.parse(variantJsonScript.textContent);
    } catch (e) {
      variants = [];
    }

    const variantIdInput = productForm.querySelector('[data-variant-id]');
    const addBtn = productForm.querySelector('[data-add-btn]');
    const addBtnText = productForm.querySelector('[data-add-btn-text]');
    const priceEl = document.querySelector('.pdp__price');
    const optionButtons = productForm.querySelectorAll('[data-option-value]');
    const optionSelectedLabels = productForm.querySelectorAll('[data-option-selected]');

    const currentSelection = [];
    optionButtons.forEach((btn) => {
      const idx = parseInt(btn.getAttribute('data-option-index'), 10);
      if (btn.classList.contains('is-selected')) {
        currentSelection[idx] = btn.getAttribute('data-value');
      }
    });

    function findVariant() {
      return variants.find((v) => currentSelection.every((val, i) => v.options[i] === val));
    }

    function updateAvailability() {
      optionButtons.forEach((btn) => {
        const idx = parseInt(btn.getAttribute('data-option-index'), 10);
        const val = btn.getAttribute('data-value');
        const testSelection = [...currentSelection];
        testSelection[idx] = val;
        const matching = variants.find((v) =>
          testSelection.every((s, i) => s === undefined || v.options[i] === s)
        );
        btn.disabled = !matching;
        if (matching && !matching.available) {
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.removeAttribute('aria-disabled');
        }
      });
    }

    function formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }

    function renderPrice(variant) {
      if (!priceEl || !variant) return;
      while (priceEl.firstChild) priceEl.removeChild(priceEl.firstChild);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const current = document.createElement('span');
        current.className = 'pdp__price-current';
        current.textContent = formatMoney(variant.price);

        const compare = document.createElement('span');
        compare.className = 'pdp__price-compare';
        compare.textContent = formatMoney(variant.compare_at_price);

        priceEl.appendChild(current);
        priceEl.appendChild(compare);
      } else {
        const single = document.createElement('span');
        single.textContent = formatMoney(variant.price);
        priceEl.appendChild(single);
      }
    }

    function updateForSelection() {
      const variant = findVariant();
      if (variant) {
        if (variantIdInput) variantIdInput.value = variant.id;
        if (addBtn) {
          if (variant.available) {
            addBtn.disabled = false;
            addBtn.removeAttribute('aria-disabled');
            if (addBtnText) addBtnText.textContent = 'Add to cart';
          } else {
            addBtn.disabled = true;
            addBtn.setAttribute('aria-disabled', 'true');
            if (addBtnText) addBtnText.textContent = 'Sold out';
          }
        }
        renderPrice(variant);

        optionSelectedLabels.forEach((el, i) => {
          el.textContent = currentSelection[i] || '';
        });

        if (variant.featured_image) {
          const mainImg = document.querySelector('[data-gallery-main] img');
          if (mainImg && variant.featured_image.src) {
            const imageUrl = variant.featured_image.src.split('?')[0];
            mainImg.src = imageUrl + '?width=1200';
            mainImg.srcset =
              imageUrl + '?width=600 600w, ' +
              imageUrl + '?width=1200 1200w, ' +
              imageUrl + '?width=1800 1800w';
          }
        }
      }

      updateAvailability();
    }

    optionButtons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        if (btn.disabled) return;

        const idx = parseInt(btn.getAttribute('data-option-index'), 10);
        const val = btn.getAttribute('data-value');

        currentSelection[idx] = val;

        const siblings = productForm.querySelectorAll('[data-option-index="' + idx + '"]');
        siblings.forEach((s) => {
          s.classList.toggle('is-selected', s === btn);
          if (s === btn) s.setAttribute('aria-current', 'true');
          else s.removeAttribute('aria-current');
        });

        updateForSelection();
      });
    });

    updateAvailability();

    /* -------- PDP Add to Cart — AJAX submit, open drawer on success -------- */
    if (cartDrawer) {
      productForm.addEventListener('submit', async (event) => {
        event.preventDefault();
        const formData = new FormData(productForm);
        const originalText = addBtnText ? addBtnText.textContent : 'Add to cart';

        if (addBtn) {
          addBtn.disabled = true;
          addBtn.classList.add('is-loading');
          if (addBtnText) addBtnText.textContent = 'Adding…';
        }

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });

          if (!response.ok) {
            let message = 'Add to cart failed';
            try {
              const err = await response.json();
              message = err.description || message;
            } catch (e) {}
            throw new Error(message);
          }

          await refreshCartDrawer();
          openCartDrawer();

          if (addBtnText) addBtnText.textContent = 'Added ✓';
          setTimeout(() => {
            if (addBtn) {
              addBtn.disabled = false;
              addBtn.classList.remove('is-loading');
              if (addBtnText) addBtnText.textContent = originalText;
            }
          }, 1400);
        } catch (err) {
          console.error(err);
          if (addBtnText) addBtnText.textContent = 'Try again';
          setTimeout(() => {
            if (addBtn) {
              addBtn.disabled = false;
              addBtn.classList.remove('is-loading');
              if (addBtnText) addBtnText.textContent = originalText;
            }
          }, 1800);
        }
      });
    }
  }

  /* ============================================================
     PDP — Quantity stepper (on-PDP quantity input, separate from drawer)
     ============================================================ */
  const quantityInput = document.querySelector('[data-quantity-input]');
  const qtyDec = document.querySelector('[data-quantity-decrement]');
  const qtyInc = document.querySelector('[data-quantity-increment]');
  if (quantityInput && qtyDec && qtyInc) {
    qtyDec.addEventListener('click', () => {
      const v = parseInt(quantityInput.value, 10) || 1;
      if (v > 1) quantityInput.value = v - 1;
    });
    qtyInc.addEventListener('click', () => {
      const v = parseInt(quantityInput.value, 10) || 1;
      quantityInput.value = v + 1;
    });
  }

  /* ============================================================
     PDP — Size guide modal
     ============================================================ */
  const sizeGuideModal = document.querySelector('[data-size-guide-modal]');
  if (sizeGuideModal) {
    const openTriggers = document.querySelectorAll('[data-size-guide-open]');
    const closeTriggers = sizeGuideModal.querySelectorAll('[data-size-guide-close]');

    function openModal() {
      sizeGuideModal.setAttribute('data-open', 'true');
      sizeGuideModal.setAttribute('aria-hidden', 'false');
      document.body.setAttribute('data-modal-open', 'true');
    }
    function closeModal() {
      sizeGuideModal.removeAttribute('data-open');
      sizeGuideModal.setAttribute('aria-hidden', 'true');
      document.body.removeAttribute('data-modal-open');
    }

    openTriggers.forEach((t) => t.addEventListener('click', openModal));
    closeTriggers.forEach((t) => t.addEventListener('click', closeModal));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sizeGuideModal.getAttribute('data-open') === 'true') {
        closeModal();
      }
    });
  }

  /* ============================================================
     Header height measurement — exposes --header-height for sticky offsets
     ============================================================ */
  function measureHeader() {
    const h = document.querySelector('.site-header');
    const a = document.querySelector('.announcement');
    let total = 0;
    if (h) total += h.offsetHeight;
    if (a) total += a.offsetHeight;
    if (total > 0) {
      document.documentElement.style.setProperty('--header-height', total + 'px');
    }
  }
  measureHeader();
  window.addEventListener('resize', measureHeader);
})();
